import "./Home.css";
import { useState } from "react";
import TypeAnimation from "../../components/animation/TypeAnimation/TypeAnimation";
import HighlightCard from "../../components/cards/HighlightCard/HighlightCard";
import Title from "../../components/common/Title/Title";
import HighlightText from "../../components/texts/HighlightText/HighlightText";
import CardBlock from "../../components/cards/CardBlock/CardBlock";
import GrayMediaCard from "../../components/cards/GrayMediaCard/GrayMediaCard";
import AccordionText from "../../components/texts/AccordionText/AccordionText";
import SEO from "../../components/common/SEO/SEO";
import Lightbox from "../../components/articles/LightBox/LightBox";
import OptimizedImage from "../../components/common/OptimizedImage/OptimizedImage";
import { useTranslation, Trans } from "react-i18next";
import MarqueeLogos from "../../components/animation/MarqueeLogos/MarqueeLogos";

export default function Home() {
  const { t } = useTranslation("home");
  const [lightboxImage, setLightboxImage] = useState(null);

  const header = t("header", { returnObjects: true }) || {};
  const seo = t("seo", { returnObjects: true }) || {};
  const challenges = t("challenges", { returnObjects: true });
  const unified = t("unified", { returnObjects: true });
  const expertise = t("expertise", { returnObjects: true });
  const plan = t("plan", { returnObjects: true });
  const row1 = t("cardRows.row1", { returnObjects: true });
  const row2 = t("cardRows.row2", { returnObjects: true });
  const revenueSection = t("revenueSection", { returnObjects: true });
  const homeGraybox = t("homeGraybox", { returnObjects: true });
  const grayMedia = t("grayMediaCards", { returnObjects: true });
  const highlights = t("plan.highlights", { returnObjects: true });
  const faqHeader = t("faqHeader", { returnObjects: true });
  const faq = t("faq", { returnObjects: true });
  const industries = t("homeGraybox.items", { returnObjects: true });

  const clientLogos = [
    { src: "/images/logos/delta.webp", alt: "Delta Elektronika" },
    { src: "/images/logos/enemed.webp", alt: "Enemed" },
    { src: "/images/logos/royal_agrifirm_group.png", alt: "Royal Agrifirm Group" },
    { src: "/images/logos/james.webp", alt: "Saint James Hospital" },
    { src: "/images/logos/maypole.webp", alt: "Maypole" },
    { src: "/images/logos/toly.webp", alt: "Toly" },
    { src: "/images/logos/SPS.webp", alt: "Solid Port Solutions" },
    { src: "/images/logos/2023 MedservRegis Long.webp", alt: "MedservRegis" },
    { src: "/images/logos/AlbertaLogo.svg", alt: "Alberta" },
  ];

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        url="/"
        image={seo.image}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "AIVHY - Industry 4.0 & IIoT Solutions",
            "description": seo.description,
            "url": "/",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "/"
                }
              ]
            },
            "mainEntity": {
              "@type": "FAQPage",
              "mainEntity": faq.map((item, index) => ({
                "@type": "Question",
                "name": item.title,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": Array.isArray(item.text) ? item.text.join(' ') : item.text
                }
              }))
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Industry 4.0 Implementation Services",
            "provider": {
              "@type": "Organization",
              "name": "AIVHY"
            },
            "description": "Comprehensive Industry 4.0, IIoT and Cyber Security implementation services for manufacturing and industrial operations",
            "serviceType": "Technology Consulting",
            "areaServed": {
              "@type": "Country",
              "name": "Netherlands"
            }
          }
        ]}
      />
      <div className="home-main-container">
        <div className="header-content">
          <TypeAnimation typeTexts={header.typeTexts} />
          <div className="header-container-title">
            <Title text={header.title1} size={["4rem", "3rem", "1.5rem"]} color={"var(--color-blue)"} level="h1" />
            <Title text={header.title2} size={["2rem", "1.5rem", "1.1rem"]} color={"var(--color-orange)"} fontWeight={"500"} />
          </div>
          <p className="home-description">
            <Trans
              i18nKey="header.description"
              ns="home"
              components={[
                <a className="text-link" href="/expertise/industrial4-0" />,
                <a className="text-link" href="/expertise/iiot" />,
              ]}
            />
          </p>
        </div>
        <div className="clients-wrapper width-container" aria-labelledby="clients-marquee-heading">
          <MarqueeLogos logos={clientLogos} speed={50} />
        </div>

        <div className="width-container">
          <div style={{ height: "auto", display: "flex", flexDirection: "column", justifyContent: "center", gap: "20px" }}>
            <div className="home-content">
              <div className="home-content-text">
                <Title text={challenges.heading} color={"var(--color-blue)"} size={["2rem", "1.3rem"]} />
                <p className="paragraph-content" style={{ fontWeight: 300 }}>{challenges.intro}</p>
                <div>
                  {challenges.items.map((c, i) => (
                    <HighlightCard key={i} title={c.title} text={c.text} />
                  ))}
                </div>
              </div>

              <div className="home-content-image">
                <OptimizedImage 
                  src="/images/industries/factory.webp" 
                  className="factory-mask" 
                  alt="Factory Image"
                  priority={true}
                />
              </div>
            </div>

            <div style={{ marginTop: "10px" }}>
              <Title text={unified.title} color={"var(--color-blue)"} size={["2rem", "1.3rem"]} />
              <p className="paragraph-content" style={{ fontWeight: 300 }}>{unified.text}</p>
            </div>
          </div>
        </div>

        <div className="width-container">
          <div className="expertise-container">
            <div className="home-content">
              <div className="home-content-text">
                <Title text={expertise.title} color={"var(--color-blue)"} size={["2rem", "1.3rem"]} />
                <div>
                  {expertise.items.map((c, i) => (
                    <HighlightCard key={i} title={c.title} text={c.text} />
                  ))}
                </div>
              </div>
            </div>
            <p className="paragraph-content" style={{ fontWeight: 300 }}>{expertise.closing}</p>
          </div>
        </div>

        <div className="width-container home-plan-container height-full-screen">
          <Title text={plan.title} color={"var(--color-blue)"} size={["2rem", "1.3rem"]} />
          <div className="home-content">
            <div className="home-content-text">
              <p className="paragraph-content" style={{ fontWeight: 300 }}>{plan.paragraph}</p>

              {highlights.map((h, i) => (
                <HighlightText key={i} text={h} />
              ))}
            </div>

            <div className="home-content-image2">
              <img
                className="image-clickable"
                src={plan.image}
                alt={plan.alt}
                style={{ maxWidth: '100%', height: 'auto' }}
                onClick={() => setLightboxImage({ src: plan.image, alt: plan.alt })}
                role="button"
                tabIndex={0}
                aria-label={`Open ${plan.alt}`}
              />
            </div>
          </div>
        </div>

        <div className="width-container height-full-screen">
          <div className="home-card-row">
            {row1.map((c, i) => <CardBlock key={i} title={c.title} text={c.text} width="400px" />)}
          </div>
          <div className="home-card-row">
            {row2.map((c, i) => <CardBlock key={i} title={c.title} text={c.text} width="400px" />)}
          </div>
        </div>

        <div className="width-container height-full-screen">
          <div className="gray-media-card-container">
            <div className="home-content">
              <GrayMediaCard image={grayMedia[0].image} title={grayMedia[0].title} to={grayMedia[0].to} text={grayMedia[0].text} />
              <GrayMediaCard image={grayMedia[1].image} imageWidth={"80px"} title={grayMedia[1].title} to={grayMedia[1].to} text={grayMedia[1].text} />
            </div>

            <GrayMediaCard iconClassName="fas fa-map-marker-alt" iconStyle={{ fontSize: "60px", color: "var(--color-orange)" }} title={grayMedia[2].title} to={grayMedia[2].to} text={grayMedia[2].text} />
          </div>
        </div>

        <div className="width-container height-full-screen-target">
          <div className="home-content-start" style={{ marginBottom: "40px", alignItems: "center" }}>
            <CardBlock title={revenueSection.newRevenue_title} text={<Trans i18nKey="revenueSection.newRevenue_text" ns="home" />} width="400px" />
            <OptimizedImage 
              src="/images/industries/car_factory.webp" 
              className="circle-mask" 
              alt="Car Factory Logo"
            />
          </div>

          <div className="home-content-start" style={{ marginBottom: "40px" }}>
            <CardBlock title={revenueSection.openSolutions_title} text={<Trans i18nKey="revenueSection.openSolutions_text" ns="home" />} width="400px" />
            <CardBlock title={revenueSection.vendorIndependent_title} text={<Trans i18nKey="revenueSection.vendorIndependent_text" ns="home" />} width="400px" />
          </div>

          <div className="home-content-start" style={{ marginBottom: "40px", alignItems: "center" }}>
            <OptimizedImage 
              src="/images/industries/ICS.webp" 
              className="ICS-home-mask" 
              alt="ICS Logo"
            />
            <CardBlock title={revenueSection.existing_title} text={<Trans i18nKey="revenueSection.existing_text" ns="home" />} width="400px" />
          </div>

          <div className="home-content-start" style={{ marginBottom: "40px", alignItems: "center" }}>
            <CardBlock title={revenueSection.quick_title} text={<Trans i18nKey="revenueSection.quick_text" ns="home" />} width="400px" />
            <OptimizedImage 
              src="/images/industries/smart_factory.webp" 
              className="industry-mask" 
              alt="Smart Factory Logo"
            />
          </div>
        </div>

        <div className="width-container">
          <div className="home-graybox">
            <h2 className="home-graybox-title">{homeGraybox.title}</h2>
            <div className="home-graybox-content">
              <div className="home-graybox-columns">
                <div className="home-graybox-col">
                  <div className="home-graybox-item">
                    <i className="fas fa-car-side" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span>{industries[0]}</span>
                  </div>
                  <div className="home-graybox-item">
                    <i className="fas fa-building" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span className="industries-item">{industries[1]}</span>
                  </div>
                  <div className="home-graybox-item">
                    <i className="fas fa-ship" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span>{industries[2]}</span>
                  </div>
                </div>

                <div className="home-graybox-col">
                  <div className="home-graybox-item">
                    <i className="fas fa-industry" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span>{industries[3]}</span>
                  </div>
                  <div className="home-graybox-item">
                    <i className="fas fa-warehouse" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span>{industries[4]}</span>
                  </div>
                  <div className="home-graybox-item">
                    <i className="fas fa-truck-loading" style={{ fontSize: "30px", color: "var(--color-orange)" }} />
                    <span>{industries[5]}</span>
                  </div>
                </div>
              </div>

              <div className="home-graybox-image">
                <OptimizedImage 
                  src="/images/graphs/map_world.webp" 
                  alt="Map Architecture" 
                  className="home-graybox-bigimg"
                />
              </div>
            </div>

            <p className="home-graybox-text">{homeGraybox.text}</p>
          </div>
        </div>

        <div className="width-container question-container" style={{ display: "flex" }}>
          <div className="question-title">
            <Title text={faqHeader.title} size={["2rem", "1.5rem", "1.3rem"]} color={"var(--color-orange)"} />
            <Title text={faqHeader.subtitle} fontWeight={"300"} size={["1.4rem", "1.2rem", "1rem"]} color={"var(--color-blue)"} />
          </div>

          <div style={{ width: "100%" }}>
            {faq.map((f, i) => (
              <AccordionText key={i} title={f.title} text={f.text}/>
            ))}
          </div>
        </div>
      </div>
      <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
    </>
  );
}